import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import GoBackButton from '@/common/components/GoBackButton';
import MaskedTextField from '@/common/components/MaskedTextField';
import Spinner from '@/common/components/Spinner'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { PermissionData } from '@/common/types/permissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEstablishmentPermissions } from '@/features/establishmentRoles/hooks/useEstablishmentPermissions'
import { useEstablishmentRole } from '@/features/establishmentRoles/hooks/useEstablishmentRole'
import { useUpdateEstablishmentRole } from '@/features/establishmentRoles/hooks/useUpdateEstablishmentRole'
import { roleSchema, RoleValidationSchema } from '@/features/tcardRoles/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeHelpIcon, ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

const Update = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [search, setSearch] = useDebounce('')
  const [selectedPermissions, setSelectedPermissions] = useState<{ permission_id: string }[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: initialRole, isLoading, isFetched: initialRoleIsFetched } = useEstablishmentRole({ id: id || '' })

  const {
    data: permissions,
    meta,
    isFetching: permissionsIsFetching,
    fetchNextPage,
  } = useEstablishmentPermissions<PermissionData[]>({
    filters: { search },
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RoleValidationSchema>({
    resolver: zodResolver(roleSchema),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateEstablishmentRolePermission = !isUserPermissionsPending ? hasUserPermission('Admin::Establishment::Roles::Update') : true

  const { mutate: updateEstablishmentRole, isPending } = useUpdateEstablishmentRole({
    roleId: id || '',
    initialRole,
    onSuccess: () => {
      navigate(`/establishment-roles/${id}`)
    },
    onError: FormTransformers.errorsTransformer<RoleValidationSchema>({ setError }),
  })

  const onSubmit: SubmitHandler<RoleValidationSchema> = (data) =>
    updateEstablishmentRole({ form: data, selectedPermissions })

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || permissionsIsFetching) {
      return false
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    if (isToFetchNextPage()) {
      fetchNextPage()
    }
  }

  useEffect(() => {
    if (initialRoleIsFetched) {
      setSelectedPermissions(
        initialRole?.permission_roles.map((permission) => ({ permission_id: permission.permission.id })) || [],
      )
    }
  }, [initialRoleIsFetched])

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateEstablishmentRolePermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton
              fallback={`/establishment-roles`}
              searchLastRouteFromFallback
            />
            <p className="text-white-400 font-normal">Cargos e permissões - Estabelecimento</p>
            <ChevronsRightIcon color="#ffffffb3" />
            {!isLoading && <h2 className="font-bold">{initialRole?.display_name}</h2>}
          </div>
          <div className="flex gap-4 items-center">
            <Link to={`/establishment-roles/${id}`}>
              <Button type="button" variant="outline-error">
                Cancelar
              </Button>
            </Link>
            <Button type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
              {isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}
            </Button>
          </div>
        </div>
        {(isLoading || isUserPermissionsPending) ? (
          <Box>Carregando...</Box>
        ) : (
          <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
            <p className="text-lg font-bold">Cargo</p>
            <TextField
              label="Nome do cargo"
              maxLength={64}
              defaultValue={initialRole?.display_name}
              errorMessage={errors.display_name?.message || undefined}
              {...register('display_name')}
            />
            <TextField
              label="Descrição do cargo"
              maxLength={256}
              defaultValue={initialRole?.description}
              errorMessage={errors.description?.message || undefined}
              {...register('description')}
            />
            <MaskedTextField
              mask={'00'}
              label='Nível'
              placeholder='0'
              defaultValue={initialRole?.level || ''}
              errorMessage={errors.level?.message || undefined}
              {...register('level')}
            />
            <Box className="flex flex-col gap-4">
              <p>Permissões</p>
              <TextField
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar permissões"
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              />
              <div
                ref={containerRef}
                onScroll={handleScroll}
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 overflow-auto min-h-14 max-h-[calc(100vh-566px)]"
              >
                {permissions?.map((permission) => (
                  <div key={permission.id} className="flex gap-2 items-center">
                    <Checkbox
                      name={permission.display_name}
                      label={permission.display_name}
                      labelClassName="max-w-[360px] text-wrap break-words"
                      checked={selectedPermissions.find((value) => value.permission_id === permission.id) ? true : false}
                      onChange={(e) => {
                        if ((e.target as HTMLInputElement).checked) {
                          setSelectedPermissions([...selectedPermissions, { permission_id: permission.id }])
                        } else {
                          setSelectedPermissions(
                            selectedPermissions.filter((value) => value.permission_id !== permission.id),
                          )
                        }
                      }}
                    />
                    <div data-tooltip-id={permission.id} className="flex gap-1 items-center text-warning">
                      <BadgeHelpIcon strokeWidth={1} size={14} />
                      <p className="font-light text-[8px] italic">Informações</p>
                    </div>
                    <Tooltip id={permission.id}>{permission.description}</Tooltip>
                  </div>
                ))}
              </div>
            </Box>
          </Box>
        )}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Update
